/**
 * The clock behind a sign-in's countdown (slice 3b spec §5.1): a tick a
 * second while anything waits, on the server's time (`skew`: the server's
 * clock less this browser's). At zero the row is hidden at once
 * (`liveWaits`); a second later the page refetches, so the server's tidy
 * runs past its 10 minutes and releases the connection. While a passed wait
 * is still listed (a refetch a moment early finds it not yet stale), it
 * refetches again every 5 s, at most 5 times (Devin on #254); once every
 * listed wait has spent its tries, the clock stops. `fired` outlives a
 * restart, as each refetch's new list restarts the clock.
 */

/** After zero, before the refetch: the server tidies only once a link is past its 10 minutes. */
const GRACE_MS = 1_000;
/** Between refetches for a wait still listed, and how many in all. */
const RETRY_MS = 5_000;
const TRIES = 5;

export type Fired = Map<number, { at: number; tries: number }>;

export function startWaitClock(o: {
  ends: number[];
  skew: number;
  fired: Fired;
  onTick: (now: number) => void;
  onPassed: () => void;
}): () => void {
  const tick = () => {
    const now = Date.now() + o.skew;
    o.onTick(now);
    const due = o.ends.filter((end) => {
      const last = o.fired.get(end);
      if (end + GRACE_MS > now) return false;
      return !last || (last.tries < TRIES && now - last.at >= RETRY_MS);
    });
    for (const end of due) o.fired.set(end, { at: now, tries: (o.fired.get(end)?.tries ?? 0) + 1 });
    if (due.length > 0) o.onPassed();
    // Every wait spent its tries: nothing left to count down or refetch (Devin on #254).
    if (o.ends.every((end) => (o.fired.get(end)?.tries ?? 0) >= TRIES)) clearInterval(timer);
  };
  const timer = setInterval(tick, 1_000);
  tick();
  return () => clearInterval(timer);
}
